import * as React from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DashboardLayout from "./DashboardLayout";

const LOCATIONS = ["Remote", "Gurugram", "Delhi", "Other"] as const;

const DEPARTMENTS = [
  "Technology",
  "Human Resource",
  "Customer Service",
  "Sales & Account Management",
  "Marketing",
  "Custom",
] as const;

type Department = (typeof DEPARTMENTS)[number];

const SKILLS_BY_DEPARTMENT: Record<Department, string[]> = {
  Technology: [
    "React",
    "JavaScript",
    "TypeScript",
    "Node.js",
    "Express",
    "MongoDB",
    "Python",
    "Django",
    "Java",
    "C++",
    "AWS",
    "Docker",
    "SQL",
    "MySQL",
    ".NET",
    "C#",
    "PostgreSQL",
    "Azure",
    "Spring Boot",
    "PHP",
    "Ruby on Rails",
    "Go",
    "Kubernetes",
    "Redis",
    "GraphQL",
    "REST API",
    "CI/CD",
    "Jenkins",
    "GCP",
    "Firebase",
    "Scala",
    "Perl",
    "Rust",
    "Elixir",
    "Laravel",
    "ASP.NET",
    "VB.NET",
    "Oracle",
    "PL/SQL",
    "PowerShell",
    "Bash",
    "Shell Scripting",
    "Selenium",
    "JUnit",
    "Mocha",
    "Chai",
    "Jest",
    "Cypress",
    "HTML",
    "CSS",
    "SASS",
    "LESS",
    "Webpack",
    "Vite",
    "Redux",
    "MobX",
    "RxJS",
    "Flutter",
    "Dart",
    "Swift",
    "Objective-C",
    "Kotlin",
    "Android",
    "iOS",
    "Unity",
    "Unreal Engine",
    "MATLAB",
    "R",
    "Hadoop",
    "Spark",
    "Tableau",
    "Power BI",
    "ETL",
    "Data Warehousing",
    "BigQuery",
    "Snowflake",
    "Looker",
    "Airflow",
    "Terraform",
    "Ansible",
    "Puppet",
    "Chef",
    "Linux",
    "Windows Server",
    "Active Directory",
    "Networking",
    "TCP/IP",
    "Firewalls",
    "Load Balancing",
    "Microservices",
    "Service Mesh",
    "API Gateway",
    "OAuth",
    "SAML",
    "SSO",
    "Security",
    "Penetration Testing",
    "DevOps",
    "Agile",
    "Scrum",
  ],
  "Human Resource": [
    "Recruitment",
    "Payroll",
    "Onboarding",
    "Employee Engagement",
    "HR Policies",
    "Performance Management",
    "Talent Acquisition",
    "Compensation & Benefits",
    "HRIS",
    "Employee Relations",
    "Training & Development",
    "Compliance",
    "HR Analytics",
  ],
  "Customer Service": [
    "Customer Support",
    "CRM",
    "Problem Solving",
    "Communication",
    "Ticketing Systems",
    "Live Chat",
    "Call Handling",
    "Conflict Resolution",
    "Empathy",
    "Product Knowledge",
    "Service Recovery",
    "Customer Satisfaction",
  ],
  "Sales & Account Management": [
    "Lead Generation",
    "Account Management",
    "Negotiation",
    "B2B Sales",
    "Presentation",
    "Client Relationship",
    "Pipeline Management",
    "Sales Strategy",
    "Upselling",
    "Cross-selling",
    "CRM",
    "Sales Forecasting",
    "Prospecting",
    "Closing Deals",
  ],
  Marketing: [
    "SEO",
    "Content Writing",
    "Google Ads",
    "Social Media",
    "Analytics",
    "Email Marketing",
    "Brand Management",
    "Market Research",
    "Campaign Management",
    "Copywriting",
    "Digital Marketing",
    "Event Planning",
    "PR",
    "Influencer Marketing",
  ],
  Custom: [],
};

const KNOWN_LANGUAGES = ["English", "Hindi", "Other"];

export interface JobType {
  id: string;
  title: string;
  company: string;
  location: string;
  numPositions: number;
  department: Department;
  customDepartment?: string;
  skills?: string[];
  knownLanguages?: string[];
  requirementDesc: string;
  jd: string;
  requirements?: string[];
}

const JOBS_KEY = "admin_jobs";

function SkillDropdown(props: {
  department: Department;
  value: string[];
  onChange: (skills: string[]) => void;
  allSkills: string[];
}) {
  const [search, setSearch] = React.useState("");
  const filtered = props.allSkills.filter(
    (s) =>
      s.toLowerCase().includes(search.toLowerCase()) && !props.value.includes(s)
  );

  return (
    <div className="space-y-2">
      <input
        className="w-full border rounded p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder="Search skills..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        {props.value.map((skill) => (
          <span
            key={skill}
            className="bg-blue-100 text-blue-800 px-2 py-1 rounded flex items-center text-sm"
          >
            {skill}
            <button
              type="button"
              className="ml-1 text-red-500 hover:text-red-700"
              onClick={() =>
                props.onChange(props.value.filter((s) => s !== skill))
              }
              aria-label={`Remove ${skill}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="max-h-32 overflow-y-auto border rounded p-2 bg-gray-50">
        {filtered.length === 0 && (
          <div className="text-gray-400 text-sm">No skills found</div>
        )}
        {filtered.map((skill) => (
          <button
            key={skill}
            type="button"
            className="block w-full text-left px-2 py-1 hover:bg-blue-100 rounded text-sm"
            onClick={() => props.onChange([...props.value, skill])}
          >
            {skill}
          </button>
        ))}
      </div>
    </div>
  );
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

function Modal({ isOpen, onClose, title, children }: ModalProps) {
  const modalRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] transition-opacity duration-300">
      <div
        className="relative w-[90vw] max-w-[90vw] bg-white rounded-lg shadow-2xl p-6 transform transition-all duration-300 scale-100 opacity-100"
        role="dialog"
        aria-labelledby="modal-title"
        aria-modal="true"
      >
        <button
          className="absolute -top-3 -right-3 bg-white rounded-full w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 text-xl shadow-md border border-gray-200"
          onClick={onClose}
          aria-label="Close modal"
        >
          &times;
        </button>
        <div ref={modalRef}>
          <h3 id="modal-title" className="text-lg font-bold mb-4">
            {title}
          </h3>
          <div className="max-h-[70vh] overflow-y-auto">{children}</div>
        </div>
      </div>
    </div>
  );
}
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
console.log("Base URL:", API_BASE_URL);

function countWords(text: string) {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

// Skeleton loader for jobs list
function JobListSkeleton() {
  // Show 3 skeleton job cards
  return (
    <div>
      {[1, 2, 3].map((i) => (
        <div key={i} className="mb-4 border rounded animate-pulse bg-gray-50">
          <div className="flex justify-between items-center px-4 py-2">
            <div className="h-5 w-32 bg-gray-200 rounded" />
            <div className="h-4 w-16 bg-gray-200 rounded" />
          </div>
          <div className="px-4 pb-4">
            <div className="mb-2 h-4 w-40 bg-gray-200 rounded" />
            <div className="mb-2 h-4 w-32 bg-gray-200 rounded" />
            <div className="mb-2 h-4 w-48 bg-gray-200 rounded" />
            <div className="mb-2 h-4 w-36 bg-gray-200 rounded" />
            <div className="mb-2 h-4 w-56 bg-gray-200 rounded" />
            <div className="mb-2 h-4 w-56 bg-gray-200 rounded" />
            <div className="mb-2 h-4 w-44 bg-gray-200 rounded" />
            <div className="flex gap-2 mt-4">
              <div className="h-8 w-16 bg-gray-200 rounded" />
              <div className="h-8 w-16 bg-gray-200 rounded" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function JobManager() {
  const [jobs, setJobs] = React.useState<JobType[]>([]);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [newJob, setNewJob] = React.useState<JobType | null>(null);
  const [isEdit, setIsEdit] = React.useState(false);

  // Validation state for word count errors
  const [descError, setDescError] = React.useState<string>("");
  const [jdError, setJdError] = React.useState<string>("");

  // Loading state for jobs
  const [loading, setLoading] = React.useState<boolean>(true);

  React.useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/get_jobs.php`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.jobs)) {
          const parsedJobs: JobType[] = data.jobs.map((j: any) => ({
            id: j.job_id,
            title: j.title,
            company: j.company,
            location: j.location,
            numPositions: j.num_positions,
            department: j.department,
            customDepartment: j.custom_department || "",
            skills: j.skills ? JSON.parse(j.skills) : [],
            knownLanguages: j.known_languages
              ? JSON.parse(j.known_languages)
              : [],
            requirementDesc: j.requirement_desc,
            jd: j.jd,
            requirements: j.requirements ? JSON.parse(j.requirements) : [],
          }));
          setJobs(parsedJobs);

          console.log("Fetched jobs:", parsedJobs);
        } else {
          setJobs([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch jobs:", err);
        setJobs([]);
        setLoading(false);
      });
  }, []);

  function openAddModal() {
    setIsEdit(false);
    setNewJob({
      id: "",
      title: "",
      company: "",
      location: LOCATIONS[0],
      numPositions: 1,
      department: DEPARTMENTS[0],
      customDepartment: "",
      skills: [],
      knownLanguages: [],
      requirementDesc: "",
      jd: "",
      requirements: [""],
    });
    setDescError("");
    setJdError("");
    setModalOpen(true);
  }

  function openEditModal(job: JobType) {
    setIsEdit(true);
    setNewJob({
      ...job,
      skills: Array.isArray(job.skills) ? job.skills : [],
      knownLanguages: Array.isArray(job.knownLanguages)
        ? job.knownLanguages
        : [],
      requirements:
        Array.isArray(job.requirements) && job.requirements.length > 0
          ? job.requirements
          : [""],
    });
    setDescError("");
    setJdError("");
    setModalOpen(true);
  }

  async function handleSave() {
    if (!newJob) return;

    // Word count validation for requirementDesc and jd
    const descWords = countWords(newJob.requirementDesc || "");
    const jdWords = countWords(newJob.jd || "");
    let hasError = false;

    if (descWords < 100 || descWords > 150) {
      setDescError(
        "Requirement Description must be between 100 and 150 words. (Current: " +
          descWords +
          ")"
      );
      hasError = true;
    } else {
      setDescError("");
    }

    if (jdWords < 100 || jdWords > 150) {
      setJdError(
        "Job Description must be between 100 and 150 words. (Current: " +
          jdWords +
          ")"
      );
      hasError = true;
    } else {
      setJdError("");
    }

    if (
      !newJob.title.trim() ||
      !newJob.location ||
      !newJob.department ||
      !newJob.jd.trim()
    ) {
      toast.error("Please fill all required fields.", {
        containerId: "modal-toast",
      });
      console.warn("Form validation failed", newJob);
      return;
    }

    if (hasError) {
      toast.error("Please fix the errors in the form.", {
        containerId: "modal-toast",
      });
      return;
    }

    // Generate a random job id
    const randomId = `job-${Math.floor(Math.random() * 1000000000)}`;
    const safeJob: JobType = {
      ...newJob,
      id: newJob.id && isEdit ? newJob.id : randomId,
      skills: Array.isArray(newJob.skills) ? newJob.skills : [],
      knownLanguages: Array.isArray(newJob.knownLanguages)
        ? newJob.knownLanguages
        : [],
      requirements:
        Array.isArray(newJob.requirements) && newJob.requirements.length > 0
          ? newJob.requirements
          : [""],
    };
    console.log("Submitting job data:", safeJob);
    const endpoint = isEdit ? "edit_job.php" : "save_jobs.php";
    console.log("Using endpoint:", endpoint);

    try {
      const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(safeJob),
      });

      // Explicitly check for HTTP errors and try to parse body for diagnostics
      if (!response.ok) {
        let errText = await response.text();
        console.error("HTTP error status:", response.status, "Body:", errText);
        toast.error(
          `Server error (${response.status}). Please try again later.`,
          { containerId: "modal-toast" }
        );
        return;
      }

      let data;
      try {
        data = await response.json();
      } catch (parseErr) {
        console.error("Failed to parse JSON response:", parseErr);
        toast.error("Received invalid response from server.", {
          containerId: "modal-toast",
        });
        return;
      }

      console.log("API response:", data);
      if (data.success) {
        toast.success(
          isEdit ? "Job updated successfully!" : "Job saved successfully!",
          { containerId: "modal-toast" }
        );
        if (isEdit) {
          setJobs(jobs.map((j) => (j.id === safeJob.id ? safeJob : j)));
        } else {
          setJobs([...jobs, safeJob]);
        }
        window.localStorage.setItem(
          JOBS_KEY,
          JSON.stringify(
            isEdit
              ? jobs.map((j) => (j.id === safeJob.id ? safeJob : j))
              : [...jobs, safeJob]
          )
        );
        setModalOpen(false);
        setNewJob(null);
      } else {
        toast.error(
          `Failed to ${isEdit ? "update" : "save"} job: ` +
            (data.error || "Unknown error"),
          { containerId: "modal-toast" }
        );
      }
    } catch (err: any) {
      console.error("API or network error:", err);
      toast.error(
        "Network or server error. Please try again later.",
        { containerId: "modal-toast" }
      );
    }
  }

  function handleDelete(id: string) {
    fetch(`${API_BASE_URL}/delete_job.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          toast.success("Job deleted successfully!", {
            containerId: "modal-toast",
          });
          const updatedJobs = jobs.filter((j) => j.id !== id);
          console.log("Updated jobs after deletion:", updatedJobs);
          setJobs(updatedJobs);
          window.localStorage.setItem(JOBS_KEY, JSON.stringify(updatedJobs));
        } else {
          toast.error(
            "Failed to delete job: " + (data.error || "Unknown error"),
            { containerId: "modal-toast" }
          );
        }
      })
      .catch((err) => {
        console.error("Delete API error:", err);
        toast.error("Network error. Please try again.", {
          containerId: "modal-toast",
        });
      });
  }

  function handleField<K extends keyof JobType>(key: K, value: JobType[K]) {
    setNewJob((j) => (j ? { ...j, [key]: value } : null));
    // Live validation for requirementDesc and jd
    if (key === "requirementDesc") {
      const wc = countWords(value as string);
      if (wc < 100 || wc > 150) {
        setDescError(
          "Requirement Description must be between 100 and 150 words. (Current: " +
            wc +
            ")"
        );
      } else {
        setDescError("");
      }
    }
    if (key === "jd") {
      const wc = countWords(value as string);
      if (wc < 100 || wc > 150) {
        setJdError(
          "Job Description must be between 100 and 150 words. (Current: " +
            wc +
            ")"
        );
      } else {
        setJdError("");
      }
    }
  }

  function getSkillOptions() {
    if (!newJob) return [];
    if (newJob.department === "Custom") return [];
    return SKILLS_BY_DEPARTMENT[newJob.department] || [];
  }

  function renderModalContent() {
    if (!newJob) return null;
    const skillOptions = getSkillOptions();
    const showCustomDept = newJob.department === "Custom";
    const skillsArr = Array.isArray(newJob.skills) ? newJob.skills : [];
    const knownLangArr = Array.isArray(newJob.knownLanguages)
      ? newJob.knownLanguages
      : [];
    const requirementsArr =
      Array.isArray(newJob.requirements) && newJob.requirements.length > 0
        ? newJob.requirements
        : [""];
    const descWordCount = countWords(newJob.requirementDesc || "");
    const jdWordCount = countWords(newJob.jd || "");
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block mb-1 text-sm font-medium">Job Title</label>
          <input
            className="w-full border rounded p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={newJob.title}
            onChange={(e) => handleField("title", e.target.value)}
            required
            aria-required="true"
          />
        </div>
        <div>
          <label className="block mb-1 text-sm font-medium">Location</label>
          <select
            className="w-full border rounded p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={newJob.location}
            onChange={(e) => handleField("location", e.target.value)}
          >
            {LOCATIONS.map((loc) => (
              <option key={loc} value={loc}>
                {loc}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-1 text-sm font-medium">
            Number of Positions
          </label>
          <input
            type="number"
            min={1}
            className="w-full border rounded p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={newJob.numPositions}
            onChange={(e) =>
              handleField("numPositions", Math.max(1, Number(e.target.value)))
            }
            required
            aria-required="true"
          />
        </div>
        <div>
          <label className="block mb-1 text-sm font-medium">Department</label>
          <select
            className="w-full border rounded p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={newJob.department}
            onChange={(e) => {
              const dep = e.target.value as Department;
              handleField("department", dep);
              if (dep !== "Custom") handleField("customDepartment", "");
              handleField("skills", []);
            }}
          >
            {DEPARTMENTS.map((dep) => (
              <option key={dep} value={dep}>
                {dep}
              </option>
            ))}
          </select>
          {showCustomDept && (
            <input
              className="w-full border rounded p-2 mt-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter custom department"
              value={newJob.customDepartment || ""}
              onChange={(e) => handleField("customDepartment", e.target.value)}
            />
          )}
        </div>
        <div className="md:col-span-2">
          <label className="block mb-1 text-sm font-medium">Skill Set</label>
          <SkillDropdown
            department={newJob.department}
            value={skillsArr}
            onChange={(skills) => handleField("skills", skills)}
            allSkills={showCustomDept ? [] : skillOptions}
          />
          {showCustomDept && (
            <div className="mt-2">
              <input
                className="w-full border rounded p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Add custom skills (comma separated)"
                value={skillsArr.join(", ")}
                onChange={(e) => {
                  const arr = e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean);
                  handleField("skills", arr);
                }}
              />
            </div>
          )}
        </div>
        <div className="md:col-span-2">
          <label className="block mb-1 text-sm font-medium">
            Known Languages
          </label>
          <div className="flex flex-wrap gap-4">
            {KNOWN_LANGUAGES.map((lang) => (
              <label key={lang} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={knownLangArr.includes(lang)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleField("knownLanguages", [...knownLangArr, lang]);
                    } else {
                      handleField(
                        "knownLanguages",
                        knownLangArr.filter((l: string) => l !== lang)
                      );
                    }
                  }}
                />
                {lang}
              </label>
            ))}
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="block mb-1 text-sm font-medium">
            Requirement Description
          </label>
          <textarea
            className={`w-full border rounded p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              descError ? "border-red-500" : ""
            }`}
            value={newJob.requirementDesc}
            onChange={(e) => handleField("requirementDesc", e.target.value)}
            placeholder="Type here (min 100 words, max 150 words)"
            rows={4}
            required
            aria-required="true"
          />
          <div className="flex justify-between items-center mt-1">
            <span
              className={`text-xs ${
                descError ? "text-red-600" : "text-gray-500"
              }`}
            >
              {descError
                ? descError
                : `Word count: ${descWordCount} (min 100, max 150)`}
            </span>
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="block mb-1 text-sm font-medium">
            Job Description
          </label>
          <textarea
            className={`w-full border rounded p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              jdError ? "border-red-500" : ""
            }`}
            value={newJob.jd}
            onChange={(e) => handleField("jd", e.target.value)}
            placeholder="Type here (min 100 words, max 150 words). To specify the essential skills, knowledge, abilities, and personal characteristics needed for successful performance of the job"
            rows={5}
            required
            aria-required="true"
          />
          <div className="flex justify-between items-center mt-1">
            <span
              className={`text-xs ${
                jdError ? "text-red-600" : "text-gray-500"
              }`}
            >
              {jdError
                ? jdError
                : `Word count: ${jdWordCount} (min 100, max 150)`}
            </span>
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="block mb-1 text-sm font-medium">
            Requirements (List)
          </label>
          {requirementsArr.map((req: string, idx: number) => (
            <div key={idx} className="flex mb-2">
              <input
                className="flex-1 border rounded p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={req}
                onChange={(e) =>
                  handleField(
                    "requirements",
                    requirementsArr.map((r: string, i: number) =>
                      i === idx ? e.target.value : r
                    )
                  )
                }
              />
              {requirementsArr.length > 1 && (
                <button
                  type="button"
                  className="ml-2 px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                  onClick={() =>
                    handleField(
                      "requirements",
                      requirementsArr.filter(
                        (_: string, i: number) => i !== idx
                      )
                    )
                  }
                  aria-label="Remove requirement"
                >
                  -
                </button>
              )}
              {idx === requirementsArr.length - 1 && (
                <button
                  type="button"
                  className="ml-2 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                  onClick={() =>
                    handleField("requirements", [...requirementsArr, ""])
                  }
                  aria-label="Add requirement"
                >
                  +
                </button>
              )}
            </div>
          ))}
        </div>
        <button
          className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition mt-4"
          onClick={handleSave}
        >
          {isEdit ? "Update Job" : "Save Job"}
        </button>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Jobs Pages</h2>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700"
          onClick={openAddModal}
        >
          Add New Job <span className="font-extrabold"> +</span>
        </button>
      </div>
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={isEdit ? "Edit Job" : "Add New Job"}
      >
        {renderModalContent()}
      </Modal>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        containerId="modal-toast"
      />
      <div className="bg-white rounded shadow p-4">
        {loading ? (
          <JobListSkeleton />
        ) : jobs.length === 0 ? (
          <div className="text-center p-4">No jobs found.</div>
        ) : (
          jobs.map((job: JobType) => {
            const skillsArr = Array.isArray(job.skills) ? job.skills : [];
            const knownLangArr = Array.isArray(job.knownLanguages)
              ? job.knownLanguages
              : [];
            const requirementsArr =
              Array.isArray(job.requirements) && job.requirements.length > 0
                ? job.requirements
                : [];
            return (
              <details key={job.id} className="mb-4 border rounded">
                <summary className="font-bold text-lg cursor-pointer flex justify-between items-center px-4 py-2 bg-white-100">
                  <span>{job.title}</span>
                  <span className="text-sm text-gray-500">{job.location}</span>
                </summary>
                <div className="mt-2 px-4 pb-4">
                  <div className="mb-2">
                    <strong>Department:</strong>{" "}
                    {job.department === "Custom"
                      ? job.customDepartment
                      : job.department}
                  </div>
                  <div className="mb-2">
                    <strong>Number of Positions:</strong> {job.numPositions}
                  </div>
                  <div className="mb-2">
                    <strong>Skill Set:</strong> {skillsArr.join(", ")}
                  </div>
                  <div className="mb-2">
                    <strong>Known Languages:</strong> {knownLangArr.join(", ")}
                  </div>
                  <div className="mb-2">
                    <strong>Requirement Description:</strong>{" "}
                    {job.requirementDesc
                      ? job.requirementDesc.split(" ").slice(0, 20).join(" ") +
                        (job.requirementDesc.split(" ").length > 20
                          ? "..."
                          : "")
                      : ""} 
                  </div>
                  <div className="mb-2">
                    <strong>Job Description:</strong>{" "}
                    {job.jd
                      ? job.jd.split(" ").slice(0, 20).join(" ") +
                        (job.jd.split(" ").length > 20 ? "..." : "")
                      : ""}
                  </div>
                  <div className="mb-2">
                    <strong>Requirements:</strong>
                    <ul className="list-disc ml-6">
                      {requirementsArr.map((req: string, idx: number) => (
                        <li key={idx}>{req}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      className="bg-yellow-500 text-white px-2 py-1 rounded hover:bg-yellow-600"
                      onClick={() => openEditModal(job)}
                    >
                      Edit
                    </button>
                    <button
                      className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                      onClick={() => handleDelete(job.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </details>
            );
          })
        )}
      </div>
    </DashboardLayout>
  );
}

export default JobManager;
